/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';
import { Redirect, type Routes } from 'react-resource-router';

import { fg } from '@atlaskit/platform-feature-flags';

import { Home } from '@atlassian/twg-microsite-experience';
import { getConfig } from '@atlassian/twg-microsite-experience/config';

const homeRoute = {
	name: 'HOME',
	path: '/',
	exact: true,
	component: Home,
} as const;

// Lazily load the CLI page so its chunk is split out of the main bundle and is
// only fetched when the gate is on (the dynamic import fires the first time
// `CliPage` renders, which only happens in the gate-on branch of `CliRoute`).
const CliPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite_cli-page" */ '@atlassian/twg-microsite-experience/cli'
	).then((module) => ({ default: module.CliPage })),
);

/**
 * Gated route component for `/cli`. The gate is evaluated here rather than at
 * module scope because this component renders inside `<Router>`, nested under
 * `<UserProvider>`, which blocks until the feature gate client has initialised
 * so the check resolves against real values. Evaluating it at module scope
 * would run at import time (before the gate client exists) and always read the
 * default. Local development bypasses the gate so `/cli` remains directly
 * accessible. Outside local development, an off gate falls back to `/`.
 */
const CliRoute = (): JSX.Element =>
	getConfig().isLocalDev || fg('twg-microsite-enable-standalone-cli-page') ? (
		<Suspense fallback={null}>
			<CliPage />
		</Suspense>
	) : (
		<Redirect to="/" />
	);

const cliRoute = {
	name: 'CLI',
	path: '/cli',
	exact: true,
	component: CliRoute,
} as const;

const catchAllRoute = {
	name: 'not-found-redirect',
	path: '*', // Matches anything not caught above
	component: () => <Redirect to="/" />, // Redirect to home
};

export const routes: Routes = [homeRoute, cliRoute, catchAllRoute] as const;
